import { apiHandler } from "../buyerApiHandler";

/**
 * Refunds register (read-only). Talks straight to the Buyer API through
 * buyerApiHandler (x-api-key), like Wallets and the ₹99 Store. Refunds are
 * still issued from Order History.
 */

export interface RefundRow {
  id: number;
  order_number: string;
  created_at: string;
  status: "awaiting_approval" | "processing" | "pending" | "processed" | "failed";
  destination: "wallet" | "source";
  mode: string;
  speed: string;
  paid_amount: number;
  refund_amount: number;
  fee_amount: number;
  fee_bearer: string;
  gst_refunded: boolean;
  tax_value: number | null;
  reason: string | null;
  initiated_by: string | null;
  razorpay_refund_id: string | null;
  wallet_transaction_id: string | null;
  user_id: number | null;
  customer_name: string | null;
  customer_phone: string | null;
  /** full = whole order; partial = unavailable items (held for approval). */
  refund_type: "full" | "partial";
  items: { order_item_id: number; name: string | null; quantity: number; cancelled_value: number; cancelled_tax: number }[] | null;
  approved_by: string | null;
  approved_at: string | null;
}

export interface RefundsResponse {
  items: RefundRow[];
  summary: {
    to_wallet: number;
    to_source: number;
    gst_withheld: number;
    failed: number;
    awaiting: number;
    awaiting_overdue: number;
  };
  pagination: { page: number; limit: number; total: number; has_more: boolean };
}

export interface RefundFilters {
  order_number?: string;
  phone?: string;
  destination?: "wallet" | "source";
  /** Text inside the refund reason. */
  reason?: string;
  status?: "awaiting_approval" | "processing" | "pending" | "processed" | "failed";
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export const getRefunds = async (filters: RefundFilters): Promise<RefundsResponse> => {
  const params = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== undefined && v !== ""),
  );
  const res: any = await apiHandler("get", "/admin/refunds", undefined, { params });
  return (res?.data ?? res) as RefundsResponse;
};

/**
 * Approve a held partial refund (unavailable items): where the money goes and
 * whether the item GST goes back. Buyer API moves the money.
 */
export const approvePartialRefund = async (
  refundId: number,
  payload: {
    mode: "wallet" | "normal" | "instant_company" | "instant_buyer";
    include_gst: boolean;
    approved_by?: string;
    /** Why the refund is approved - kept with the refund. */
    reason?: string;
  },
) => {
  const res: any = await apiHandler("post", `/api/buyer/admin/refunds/${refundId}/approve`, payload, {
    headers: { "Content-Type": "application/json" },
  });
  return res?.data ?? res;
};
